import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/80 backdrop-blur-md border-b border-gray-100 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        <h1 className="text-lg font-semibold text-gray-900">Privacy Policy</h1>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Privacy Policy for Ani-Marketplace</h2>
          <p className="text-sm text-gray-500 mt-1">Effective Date: March 20, 2026</p>
        </div>

        <p className="text-gray-600 leading-relaxed">
          Ani-Marketplace ("we", "our", or "us") respects your privacy and is committed to protecting
          your personal information. This Privacy Policy explains how we collect, use, and safeguard
          your information when you use our platform.
        </p>

        <Section title="1. Information We Collect">
          <SubSection title="a) Personal Information">
            <p>When you register or use Ani-Marketplace, we may collect:</p>
            <ul>
              <li>Full name</li>
              <li>Email address</li>
              <li>Password (securely stored using encryption via Firebase Authentication)</li>
            </ul>
          </SubSection>

          <SubSection title="b) Transaction Information">
            <p>When you make a purchase on Ani-Marketplace:</p>
            <ul>
              <li>Order details and transaction status are recorded</li>
              <li>Payments are securely processed via Razorpay</li>
            </ul>
            <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <strong>Note:</strong> We do NOT store your card details, UPI credentials, or banking
              information. All payment data is handled by Razorpay.
            </div>
          </SubSection>

          <SubSection title="c) Usage Data">
            <p>We may automatically collect:</p>
            <ul>
              <li>IP address</li>
              <li>Device and browser information</li>
              <li>Pages visited and interactions</li>
            </ul>
          </SubSection>
        </Section>

        <Section title="2. How We Use Your Information">
          <p>We use your data to:</p>
          <ul>
            <li>Create and manage your account</li>
            <li>Process orders and transactions</li>
            <li>Provide customer support</li>
            <li>Improve our platform and user experience</li>
            <li>Send important updates (orders, security alerts, etc.)</li>
          </ul>
        </Section>

        <Section title="3. Cookies and Tracking Technologies">
          <p>We use cookies and similar technologies to:</p>
          <ul>
            <li>Maintain login sessions</li>
            <li>Store user preferences</li>
            <li>Analyze website usage</li>
          </ul>
          <p className="mt-3">
            You can disable cookies through your browser settings, but some features may not
            function properly.
          </p>
        </Section>

        <Section title="4. Third-Party Services">
          <p>We use trusted third-party services:</p>
          <ul>
            <li>
              <strong>Firebase (Google)</strong> — Hosting, authentication, and backend services
            </li>
            <li>
              <strong>Razorpay</strong> — Payment processing
            </li>
            <li>Analytics tools (if implemented in future)</li>
          </ul>
          <p className="mt-3">
            These services operate under their own privacy policies. We are not responsible for
            their data practices.
          </p>
        </Section>

        <Section title="5. Data Security">
          <p>We implement industry-standard security practices:</p>
          <ul>
            <li>Secure authentication via Firebase</li>
            <li>Encrypted password storage</li>
            <li>Controlled data access</li>
          </ul>
          <p className="mt-3">
            However, no online platform can guarantee 100% security.
          </p>
        </Section>

        <Section title="6. Data Sharing">
          <p className="font-medium text-gray-800">We do NOT sell your personal information.</p>
          <p className="mt-2">We may share limited data:</p>
          <ul>
            <li>With Razorpay for payment processing</li>
            <li>With Firebase for hosting and authentication</li>
            <li>When required by law or legal authorities</li>
          </ul>
        </Section>

        <Section title="7. User Rights">
          <p>You have the right to:</p>
          <ul>
            <li>Access your personal data</li>
            <li>Request correction or deletion</li>
            <li>Delete your account</li>
          </ul>
          <div className="mt-3 flex items-center gap-2 text-purple-700">
            <Mail className="w-4 h-4 flex-shrink-0" />
            <a
              href="mailto:artistinternationalnetwork@gmail.com"
              className="underline break-all"
            >
              artistinternationalnetwork@gmail.com
            </a>
          </div>
        </Section>

        <Section title="8. Data Retention">
          <p>We retain your information only as long as necessary for:</p>
          <ul>
            <li>Providing services</li>
            <li>Legal compliance</li>
            <li>Resolving disputes</li>
          </ul>
        </Section>

        <Section title="9. Changes to This Privacy Policy">
          <p>
            We may update this policy periodically. Changes will be posted on this page with an
            updated effective date.
          </p>
        </Section>

        <Section title="10. Contact Us">
          <p>For any questions or concerns:</p>
          <div className="mt-3 flex items-center gap-2 text-purple-700">
            <Mail className="w-4 h-4 flex-shrink-0" />
            <a
              href="mailto:artistinternationalnetwork@gmail.com"
              className="underline break-all"
            >
              artistinternationalnetwork@gmail.com
            </a>
          </div>
        </Section>

        <div className="border-t border-gray-200 pt-6 text-center text-sm text-gray-500">
          By using Ani-Marketplace, you agree to this Privacy Policy.
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="bg-white rounded-2xl p-6 shadow-sm space-y-3">
    <h3 className="text-base font-semibold text-gray-900 border-b border-gray-100 pb-2">{title}</h3>
    <div className="text-gray-600 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1 [&_p]:text-gray-600">
      {children}
    </div>
  </div>
);

const SubSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="space-y-2">
    <h4 className="text-sm font-medium text-gray-800">{title}</h4>
    <div className="text-gray-600 leading-relaxed space-y-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
      {children}
    </div>
  </div>
);

export default PrivacyPolicy;
